// Copyright 2025, University of Maryland, All Rights Reserved
import type { JSONSchema } from '@/api/types/schema'

export interface SectionFieldData {
  type: 'integer' | 'text' | 'enum' | 'text_list' | 'object_list' | 'score'
  value: any
  title?: string
  order?: number
  object_properties?: Record<string, { title?: string; type?: string }>
}

export interface SectionData {
  section_name: string
  title?: string
  fields: SectionFieldData[]
  order?: number
}

/**
 * Detect field type based on value structure
 */
export function detectFieldType(value: any): 'integer' | 'text' | 'enum' | 'text_list' | 'object_list' | 'score' {
  if (typeof value === 'number') {
    return 'integer'
  }
  
  if (typeof value === 'string') {
    return 'text'
  }
  
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'text_list' // default for empty arrays
    }
    
    // Check if all items are strings
    if (value.every(item => typeof item === 'string')) {
      return 'text_list'
    }
    
    // Check if all items are objects
    if (value.every(item => typeof item === 'object' && item !== null)) {
      return 'object_list'
    }
    
    // Mixed types - treat as text_list
    return 'text_list'
  }
  
  if (typeof value === 'object' && value !== null) {
    // Handle score objects with {score, score_type} structure
    if ('score' in value && 'score_type' in value) {
      return 'score'
    }
    
    // Handle legacy AIImpact objects with {type, score} structure
    if ('type' in value && 'score' in value) {
      return 'enum' // Display as enum to get proper styling
    }
    
    // Handle other objects as object_list with single item
    return 'object_list'
  }
  
  // Default to text for unknown types
  return 'text'
}

/**
 * Parse report data to extract sections dynamically
 */
export function parseAnalysisData(report_data: any, schema?: JSONSchema): SectionData[] {
  console.log('=== parseAnalysisData DEBUG ===');
  console.log('Input report_data:', report_data);
  console.log('Input schema:', schema);
  
  if (!report_data || typeof report_data !== 'object') {
    console.log('❌ No report_data or not an object');
    return []
  }

  const sections: SectionData[] = []

  // Process each top-level section
  for (const [section_name, section_value] of Object.entries(report_data)) {
    console.log(`Processing section: ${section_name}`, section_value);
    
    // Skip title field at root level
    if (section_name === 'title' || typeof section_value !== 'object' || section_value === null) {
      console.log(`❌ Skipping section ${section_name} - title field or not object`);
      continue
    }

    // Get section title from schema metadata only
    const section_title = schema?.properties?.[section_name]?.title

    const section_data: SectionData = {
      section_name,
      title: section_title,
      fields: [],
      order: (section_value as any).order || 1
    }

    // Get field titles and orders from schema if available
    const section_schema = schema?.properties?.[section_name]
    const field_metadata_from_schema = section_schema?.properties ? 
      Object.entries(section_schema.properties).reduce((acc, [field_key, field_schema]) => {
        acc[field_key] = {
          title: (field_schema as any).title,
          order: (field_schema as any).order
        }
        return acc
      }, {} as Record<string, { title?: string; order?: number }>) : {}

    // Process each field in the section
    for (const [field_key, field_value] of Object.entries(section_value as Record<string, any>)) {
      console.log(`  Processing field: ${field_key}`, field_value);
      
      // Skip metadata fields and ID fields
      if (field_key === 'icon' || field_key === 'order' || field_key === 'id' || field_key === 'section_id' || field_key.toLowerCase().includes('id')) {
        console.log(`  ❌ Skipping field ${field_key} - metadata/ID field`);
        continue
      }

      const field_type = detectFieldType(field_value)
      console.log(`  Field type detected: ${field_type}`);
      
      // Get field title and order from schema only - never use field_key as title
      const field_metadata = field_metadata_from_schema[field_key] || {}
      const field_title = field_metadata.title || undefined
      const field_order = field_metadata.order

      // Extract the actual value
      let actual_value = field_value
      if (typeof field_value === 'object' && field_value !== null && !Array.isArray(field_value)) {
        // For objects, check if it has a 'value' property, otherwise use the whole object
        if ('value' in field_value) {
          actual_value = field_value.value
        } else {
          // Keep the whole object - the renderFieldValue function will handle it safely
          actual_value = field_value
        }
      }

      // For object_list fields, extract property titles from schema
      let object_properties: Record<string, { title?: string; type?: string }> | undefined
      if (field_type === 'object_list') {
        const field_schema = section_schema?.properties?.[field_key]
        if (field_schema && 'items' in field_schema && field_schema.items && typeof field_schema.items === 'object' && 'properties' in field_schema.items) {
          object_properties = {}
          const item_properties = field_schema.items.properties as Record<string, any>
          for (const [prop_key, prop_schema] of Object.entries(item_properties)) {
            object_properties[prop_key] = {
              title: prop_schema.title,
              type: prop_schema.type
            }
          }
        }
      }

      // Add fields that have meaningful content - THIS IS THE KEY: ADD ALL FIELDS, NOT JUST ONES WITH TITLES
      section_data.fields.push({
        type: field_type,
        value: actual_value,
        title: field_title,
        order: field_order,
        object_properties
      })
      
      console.log(`  ✅ Added field ${field_key} with type ${field_type} and title ${field_title || 'none'}`);
    }

    console.log(`Section ${section_name} has ${section_data.fields.length} fields`);
    
    // Only add sections that have at least one field
    if (section_data.fields.length > 0) {
      // Sort fields within the section by their order property
      section_data.fields.sort((a, b) => (a.order || 999) - (b.order || 999))
      sections.push(section_data)
      console.log(`✅ Added section ${section_name} to results`);
    } else {
      console.log(`❌ Section ${section_name} has no fields - skipping`);
    }
  }

  console.log(`=== FINAL RESULT ===`);
  console.log(`Total sections found: ${sections.length}`);
  sections.forEach((section, index) => {
    console.log(`${index + 1}. ${section.section_name} (${section.fields.length} fields)`);
  });

  // Sort sections by order
  return sections.sort((a, b) => (a.order || 1) - (b.order || 1))
}